import {parseJWT} from '@utils/jwtUtils';
import {AUTH_USER_ROLE, AUTH_RECIPIENT_ROLE} from '@constants/user';

const SESSION_STORAGE_PREFIX = 'signer-recipientId';

interface JWTPayload {
    role?: string;
    external_id?: string;
}

function getIsClient(): boolean {
    return typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';
}

function getSessionStorageKey(transactionId: string): string {
    return `${SESSION_STORAGE_PREFIX}-${transactionId}`;
}

/**
 * Saves recipientId to sessionStorage for a specific transaction
 * Used when user role is "user" and recipientId comes from URL
 */
export function saveUserRecipientIdToSession(
    transactionId: string | null | undefined,
    recipientId: string | null | undefined
): void {
    if (!getIsClient() || !transactionId || !recipientId) {
        return;
    }

    try {
        const key = getSessionStorageKey(transactionId);
        sessionStorage.setItem(key, recipientId);
    } catch (error) {
        console.error('[recipientIdResolver] Failed to save recipientId to sessionStorage:', error);
    }
}

/**
 * Gets recipientId from sessionStorage for a specific transaction
 */
export function getUserRecipientIdFromSession(
    transactionId: string | null | undefined
): string | null {
    if (!getIsClient() || !transactionId) {
        return null;
    }

    try {
        const key = getSessionStorageKey(transactionId);
        return sessionStorage.getItem(key);
    } catch (error) {
        console.error('[recipientIdResolver] Failed to read recipientId from sessionStorage:', error);
        return null;
    }
}

/**
 * Clears recipientId from sessionStorage for a specific transaction
 */
export function clearUserRecipientIdFromSession(
    transactionId: string | null | undefined
): void {
    if (!getIsClient() || !transactionId) {
        return;
    }

    try {
        const key = getSessionStorageKey(transactionId);
        sessionStorage.removeItem(key);
    } catch (error) {
        console.error('[recipientIdResolver] Failed to clear recipientId from sessionStorage:', error);
    }
}

/**
 * Determines recipientId for signing based on user role
 * For role "user" - uses recipientId from URL parameter or sessionStorage
 * For role "recipient" - uses external_id from JWT token
 * @param accessToken - JWT access token
 * @param urlRecipientId - recipientId from URL parameter (optional)
 * @param transactionId - transaction ID for sessionStorage lookup (optional)
 * @returns recipientId for API calls or null if not available
 */
export function getRecipientIdForSigning(
    accessToken: string | null | undefined,
    urlRecipientId?: string | null,
    transactionId?: string | null
): string | null {
    if (!accessToken) {
        return null;
    }

    const tokenPayload = parseJWT(accessToken) as JWTPayload | null;
    const role = tokenPayload?.role;

    if (role === AUTH_USER_ROLE) {
        if (urlRecipientId) {
            return urlRecipientId;
        }
        return getUserRecipientIdFromSession(transactionId);
    }

    return tokenPayload?.external_id || null;
}
